﻿
import Map from 'ol/Map';
import Geometry from 'ol/geom/Geometry';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { fromExtent } from 'ol/geom/Polygon';
import { transform } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import { Extent, boundingExtent, getCenter, getSize, containsCoordinate, isEmpty } from 'ol/extent';

import { Projections } from '../models/Projections';
import { GeometryExtensions } from '../geometries/GeometryExtensions';

const { WebMercator, WGS } = Projections
export const DEGREES_180 = transform([180, 0], WGS, WebMercator)[0];
export const DEGREES_360 = 2 * DEGREES_180;
export const DEGREES_90 = 0.5 * DEGREES_180;

const _xBoundary = 2 * DEGREES_180;
const _yBoundary = transform([0, 90], WGS, WebMercator)[1];

class DateLineUtilities {    

    /**
     * Set x value between -180 and 180 degrees
     * @param x: EPSG:3857 x value (longitude)
     */
    public static setBetweenDateLines(x: number, center: number = 0) {

        while (x > (center + DEGREES_180))
            x -= DEGREES_360;

        while (x < (center - DEGREES_180))
            x += DEGREES_360;

        return x;
    }

    public static isBetweenDateLines(x: number) {
        return x >= -DEGREES_180 && x <= DEGREES_180;
    }

    public static calculateWrapOffset(x: number, center: number = 0) {
        return Math.round((x - center) / DEGREES_360);
    }

    public static wrapOffsetToDegrees(x: number) {
        return x * DEGREES_360;
    }

    /**
     * Returns offset in 360 degrees of x from the -180 to 180 degrees range
     * @param x: EPSG:3857 x value (longitude)
     */
    public static getOffsetFromDateLines(x: number) {
        var offset = 0;
        while (x < -DEGREES_180) {
            x += DEGREES_360;
            offset--;
        }

        while (x > DEGREES_180) {
            x -= DEGREES_360;
            offset++;
        }

        return offset;
    }

    /**
     * Translate x over the dateline
     * @param x: EPSG:3857 x value (longitude)
     * @param wraps
     */
    public static translateX(x: number, wraps: number) {
        return x + wraps * DEGREES_360;
    }

    /**
     * Translate geometry over the dateline, geometry changed in place
     * @param geometry 
     * @param wraps 
     */
    public static translateGeometry(geometry: Geometry, wraps: number) {
        geometry.translate(wraps * DEGREES_360, 0);
    }


    public static getPositionWithinMapViewExtent(map: Map, position: Coordinate) {
        const mapExtent = map.getView().calculateExtent(map.getSize());

        for (var i of this.generateAlternatingNumbers(9)) {
            const x = DateLineUtilities.translateX(position[0], i);
            if (containsCoordinate(mapExtent, [x, position[1]]))
                return [x, position[1]];
        }

        return position;
    }

    public static getPositionClosestToMapViewCenter(map: Map, position: Coordinate) {
        const view = map.getView()
        const center = view.getCenter() || [0, 0];
        const centerPixel = map.getPixelFromCoordinate(center);

        var closestX: number = position[0];
        var shortestDistance: number = Infinity;

        for (var i of this.generateAlternatingNumbers(9)) {
            const x = DateLineUtilities.translateX(position[0], i);
            const pixel = map.getPixelFromCoordinate([x, position[1]]);
            if (pixel?.length) {
                const distance = Math.abs(pixel[0] - centerPixel[0]);
                if (distance < shortestDistance) {
                    shortestDistance = distance;
                    closestX = x;
                }
            }
        }

        return [closestX, position[1]] as Coordinate;
    }

    public static getExtentClosestToMapViewCenter(map: Map, extent: Extent) {
        const center = this.getPositionClosestToMapViewCenter(map, getCenter(extent));
        const halfSize = getSize(extent).map(v => v / 2);

        return boundingExtent([
            [
                center[0] + halfSize[0],
                center[1] + halfSize[1],
            ],
            [
                center[0] - halfSize[0],
                center[1] - halfSize[1],
            ]
        ])
    }

    /**
     * Returns all coordinates within provided extent matching provided coordinate but 360 degrees to left and/or right
     * @param coordinate
     * @param extent
     */
    public static getWrappedCoordinatesInExtent(coordinate: Coordinate, extent: Extent) {
        if (!isEmpty(extent)) {
            let [x, y] = coordinate;

            x = this.setBetweenDateLines(x);

            var coords: Coordinate[] = [];
            var range = this.getExtentWrapRange_(extent);

            for (var i = range[0]; i <= range[1]; i++) {
                const newX = x + DEGREES_360 * i
                coords.push([newX, y]);
            }

            return coords;
        }
        else
            return [coordinate];
    }

    /**
     * Fixes coordinate if it's outside the boundary of projection (latitude) or openlayers (wrapped longitude)
     * @param coordinate
     */
    public static fixCoordinateIfOutOfBounds(coordinate: Coordinate) {
        let [x, y] = coordinate;

        if (x >= _xBoundary)
            x = x - DEGREES_360
        else if (x <= -_xBoundary)
            x = x + DEGREES_360;

        y = Math.max(Math.min(y, _yBoundary), -_yBoundary);

        if (x !== coordinate[0] || y !== coordinate[1])
            return [x, y]
        else
            return undefined;
    }

    /**
     * Merged extents into 1 extent. Makes sure extents close to dateline are grouped together.
     * @param extents
     */
    public static mergeExtentsAtDateLine(extents: Extent[]) {
        const geometry = new MultiPolygon([]);
        extents.forEach(extent => geometry.appendPolygon(fromExtent(extent)));

        const geometryExtension = new GeometryExtensions(geometry);
        geometryExtension.groupSectionsAtDateLine();
        return geometryExtension.getGeometry()?.getExtent();
    }

    /**
     * Makes sure geometry sections of multipoint/line/polygon close to dateline are grouped together.
     * @param geometry
     */
    public static groupGeometrySectionsAtDateLine(geometry: Geometry | GeoJSON.GeoJSON) {
        const geometryExtension = new GeometryExtensions();
        if (geometry instanceof Geometry)
            geometryExtension.setGeometry(geometry);
        else
            geometryExtension.setGeojson(geometry)
        geometryExtension.groupSectionsAtDateLine();
        return geometryExtension.getGeometry();
    }

    private static getExtentWrapRange_(extent: Extent) {
        return [
            Math.floor(extent[0] / DEGREES_360 + 0.5),
            Math.floor(extent[2] / DEGREES_360 + 0.5)
        ]
    }

    /**
     * Returns [0, 1, -1, 2, -2, ...]
     * @param length number of items in result
     */
    private static generateAlternatingNumbers(length: number) {
        const result: number[] = [];
        for (var i = 0; i < length; i++) {
            result.push((i % 2 === 0) ? -(i / 2) + 0 : Math.ceil(i / 2));
        }
        return result;
    }
}

export default DateLineUtilities;